import { useEffect, useMemo, useState } from "react";
import { api, Manifest, PostMortem } from "../api";
import { splitRecent } from "../lib/recent";
import OlderToggle from "./OlderToggle";

type Trend = { content: string; source_days: string[]; created_at: string } | null;

export default function PostMortems({ book }: { manifest: Manifest; book: string }) {
  const [reports, setReports] = useState<PostMortem[]>([]);
  const [trend, setTrend] = useState<Trend>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showOlder, setShowOlder] = useState(false);
  // trading_day is a plain YYYY-MM-DD; new Date() reads that as UTC midnight,
  // which is the previous evening in ET — harmless here since the cutoff is a
  // whole day boundary, and erring toward SHOWING a report is the safe side.
  const shown = useMemo(() => splitRecent(reports, (r) => r.trading_day), [reports]);

  useEffect(() => {
    if (book === "all") return; // post-mortems are written per book — see message below
    (async () => {
      try {
        setReports(await api.get<PostMortem[]>(`/api/trading/post-mortems?book=${book}`));
        setTrend(await api.get<Trend>(`/api/trading/trends?book=${book}`));
      } catch (e) { setError((e as Error).message); }
    })();
  }, [book]);

  if (book === "all") {
    return (
      <div className="card text-slate-500 text-sm font-sans">
        Post-mortems are written per book — pick a specific book above to view its library.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">Post-Mortem Library</h1>
      {error && <div className="text-down text-sm">{error}</div>}

      {trend && (
        <div className="card border-accent/40">
          <h2 className="label">Latest trend digest ({trend.source_days.length} days)</h2>
          <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans">{trend.content}</pre>
        </div>
      )}

      {(showOlder ? reports : shown.recent).map((r) => (
        <div key={r.id} className="card">
          <button className="w-full text-left flex items-center gap-3"
                  onClick={() => setOpen(open === r.id ? null : r.id)}>
            <span className="font-bold font-mono">{r.trading_day}</span>
            {r.metrics && (
              <span className="text-slate-500 text-sm">
                {String(r.metrics.proposals ?? 0)} proposals · {String(r.metrics.orders ?? 0)} orders · {String(r.metrics.fills ?? 0)} fills
              </span>
            )}
          </button>
          {open === r.id && (
            <pre className="mt-3 whitespace-pre-wrap text-sm text-slate-300 font-sans">{r.content}</pre>
          )}
        </div>
      ))}
      {shown.older.length > 0 && (
        <div className="card py-0">
          <OlderToggle count={shown.older.length} open={showOlder}
                       onToggle={() => setShowOlder(!showOlder)} noun="older post-mortems" />
        </div>
      )}
      {reports.length === 0 && (
        <div className="card text-slate-500 text-sm">
          No post-mortems yet. The review service writes one each evening after the close.
        </div>
      )}
    </div>
  );
}
